import React from 'react'
import { Badge } from '../shared/Badge'
import { AgentStats } from '../../hooks/useAgents'
import { formatDuration } from '../../utils/format'

interface AgentCardProps {
  agent: AgentStats
  onClick: () => void
}

export const AgentCard: React.FC<AgentCardProps> = ({ agent, onClick }) => {
  const isOnline = agent.status === 'online'
  const loadPercentage = Math.min((agent.openChatCount / 10) * 100, 100)
  const loadColor = loadPercentage > 80 ? 'bg-error' : loadPercentage > 50 ? 'bg-warning' : 'bg-success'

  return (
    <div
      onClick={onClick}
      className="group p-4 rounded-xl bg-surface border border-border hover:border-brand-200 dark:hover:border-brand-800 hover:shadow-sm cursor-pointer transition-all duration-150"
    >
      {/* Header: avatar, name, status */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 text-sm font-bold">
            {agent.name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-surface ${
            isOnline ? 'bg-success' : agent.status === 'on_break' ? 'bg-warning' : 'bg-gray-400'
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-txt-primary truncate">{agent.name}</h3>
          <Badge variant="status" value={agent.status} />
        </div>
      </div>

      {/* Workload bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] text-txt-secondary mb-1">
          <span>Workload</span>
          <span className="font-medium">{agent.openChatCount} chats</span>
        </div>
        <div className="h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
          <div
            className={`h-full rounded-full ${loadColor} transition-all duration-300`}
            style={{ width: `${loadPercentage}%` }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] text-txt-tertiary uppercase tracking-wider">Avg Response</p>
          <p className="text-sm font-semibold text-txt-primary">{formatDuration(agent.avgResponseTimeSeconds)}</p>
        </div>
        <div>
          <p className="text-[10px] text-txt-tertiary uppercase tracking-wider">Today</p>
          <p className="text-sm font-semibold text-txt-primary">{agent.totalChatsToday} chats</p>
        </div>
        <div>
          <p className="text-[10px] text-txt-tertiary uppercase tracking-wider">Resolution</p>
          <p className="text-sm font-semibold text-txt-primary">{formatDuration(agent.avgResolutionTimeSeconds)}</p>
        </div>
        <div>
          <p className="text-[10px] text-txt-tertiary uppercase tracking-wider">CSAT</p>
          <p className="text-sm font-semibold text-txt-primary">
            {agent.csatScore.toFixed(1)}
            <span className="text-xs text-txt-tertiary">/5.0</span>
          </p>
        </div>
      </div>
    </div>
  )
}
